import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createJob } from '../api/jobs';
import { getSettings } from '../api/settings';
import { recentFinancialYears, yearsFromOffsets } from '../format';
import { UnauthorizedError } from '../api/client';
import type { JobCreateInput } from '../types';
import { ErrorBanner, toMessage } from '../components/common';

// Default engagement types (also the seed values for the editable presets in
// Settings). "Other" reveals a free-text field.
const ENGAGEMENT_TYPES = ['Business Valuation', 'Market Appraisal'];

// Default document requirements (seed values for the editable Settings presets).
// Ticked ones become the upload-portal checklist; "Others" reveals a free field.
// The general information questionnaire is deliberately NOT here any more. It is not
// a document the client uploads, it is a form they fill in, and since 2026-09-11 it is
// one of the two tabs behind the Collect information link every client receives on
// signing. Leaving it as a tickbox meant it could be forgotten, and a forgotten
// questionnaire leaves Owners' Involvement empty and the director remuneration
// add-back at zero in a workbook that otherwise looks finished.
const TYPICAL_DOCS = [
  'Last 3 financial statements',
  'Management accounts',
  'Premises lease agreement',
  'Asset register / depreciation schedule',
  'Trust deed',
];
// Pre-ticked on form load.
const PRESELECTED_DOCS = new Set([
  'Last 3 financial statements',
  'Management accounts',
]);

// Intake form that replaces Adrien's spreadsheet.
export function JobNew() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Preset options come from org Settings (editable there), falling back to the
  // built-in defaults if Settings has not loaded / been configured.
  const [engagementTypes, setEngagementTypes] = useState<string[]>(ENGAGEMENT_TYPES);
  const [docList, setDocList] = useState<string[]>(TYPICAL_DOCS);

  const [entityName, setEntityName] = useState('');
  const [abn, setAbn] = useState('');
  const [industry, setIndustry] = useState('');
  const [engagementType, setEngagementType] = useState(ENGAGEMENT_TYPES[0]);
  const [engagementOther, setEngagementOther] = useState('');
  // Australian financial years: the four most recent COMPLETED AFYs (each ends
  // 30 June). On 14 Aug 2026 the last completed AFY is FY26, so options are
  // FY23-FY26 with the three prior years (FY23-FY25) ticked.
  const [fyOptions] = useState<string[]>(() => recentFinancialYears());
  // Which start ticked is a practice preference now, set in Settings and stored as
  // offsets from the newest year so it does not go stale each July. The [1,2,3]
  // fallback reproduces the behaviour that was hardcoded here: the three prior years,
  // with the most recent deliberately left off because its statements are usually not
  // ready when an engagement starts.
  const [checkedYears, setCheckedYears] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(fyOptions.map((fy) => [fy, false])),
  );
  const [yearsOthersOn, setYearsOthersOn] = useState(false);
  const [yearsOthersText, setYearsOthersText] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [feeQuoted, setFeeQuoted] = useState('');
  const [checkedDocs, setCheckedDocs] = useState<Record<string, boolean>>(
    Object.fromEntries(TYPICAL_DOCS.map((d) => [d, PRESELECTED_DOCS.has(d)])),
  );
  const [othersOn, setOthersOn] = useState(false);
  const [othersText, setOthersText] = useState('');

  useEffect(() => {
    let cancelled = false;
    getSettings()
      .then((s) => {
        if (cancelled) return;
        if (s.engagementTypes?.length) {
          setEngagementTypes(s.engagementTypes);
          setEngagementType((cur) => (s.engagementTypes.includes(cur) ? cur : s.engagementTypes[0]));
        }
        if (s.docRequirements?.length) {
          setDocList(s.docRequirements);
          setCheckedDocs(Object.fromEntries(s.docRequirements.map((d) => [d, PRESELECTED_DOCS.has(d)])));
        }
        // Offsets from the newest of the four, so the preference survives July.
        const ticked = yearsFromOffsets(s.financialYearOffsets ?? [1, 2, 3]);
        setCheckedYears(Object.fromEntries(fyOptions.map((fy) => [fy, ticked.includes(fy)])));
      })
      .catch(() => {
        // Settings unreachable: fall back to the years that were hardcoded here
        // before, rather than opening the form with nothing ticked.
        setCheckedYears(
          Object.fromEntries(fyOptions.map((fy) => [fy, yearsFromOffsets([1, 2, 3]).includes(fy)])),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [fyOptions]);

  function splitLines(value: string): string[] {
    return value.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
  }

  const abrUrl = entityName.trim()
    ? `https://abr.business.gov.au/Search/ResultsActive?SearchText=${encodeURIComponent(entityName.trim())}`
    : '';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const resolvedEngagement = engagementType === 'Other' ? engagementOther.trim() : engagementType;
    if (!entityName.trim() || !resolvedEngagement || !contactName.trim() || !contactEmail.trim()) {
      setError('Entity name, engagement type, contact name and contact email are required.');
      return;
    }

    const docRequirements = [
      ...docList.filter((d) => checkedDocs[d]),
      ...(othersOn ? splitLines(othersText) : []),
    ];

    const years = [
      ...fyOptions.filter((fy) => checkedYears[fy]),
      ...(yearsOthersOn ? splitLines(yearsOthersText) : []),
    ];

    const input: JobCreateInput = {
      entityName: entityName.trim(),
      abn: abn.trim() || undefined,
      industry: industry.trim() || undefined,
      engagementType: resolvedEngagement,
      financialYears: years.length ? years : undefined,
      contact: {
        name: contactName.trim(),
        email: contactEmail.trim(),
        phone: contactPhone.trim() || undefined,
      },
      feeQuotedExGst: feeQuoted.trim() ? Number(feeQuoted) : undefined,
      docRequirements: docRequirements.length ? docRequirements : undefined,
    };

    if (input.feeQuotedExGst != null && Number.isNaN(input.feeQuotedExGst)) {
      setError('Fee quoted must be a number.');
      return;
    }

    setSaving(true);
    try {
      const job = await createJob(input);
      navigate(`/jobs/${job.jobId}`, { replace: true });
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        navigate('/signin', { replace: true });
        return;
      }
      setError(toMessage(err));
      setSaving(false);
    }
  }

  return (
    <>
      <div className="toolbar">
        <h1>New job</h1>
        <button className="btn-secondary" onClick={() => navigate('/jobs')}>
          Cancel
        </button>
      </div>

      {error ? <ErrorBanner message={error} /> : null}

      <form className="card" onSubmit={submit}>
        <h3>Entity</h3>
        <div className="grid-2">
          <div className="field">
            <label htmlFor="entityName">Entity name *</label>
            <input id="entityName" value={entityName} onChange={(e) => setEntityName(e.target.value)} required />
            {abrUrl ? (
              <a className="abr-lookup" href={abrUrl} target="_blank" rel="noreferrer">
                Look up "{entityName.trim()}" on ABN Lookup ↗
              </a>
            ) : (
              <span className="abr-lookup muted">Enter an entity name to check it on ABN Lookup</span>
            )}
          </div>
          <div className="field">
            <label htmlFor="abn">ABN</label>
            <input id="abn" value={abn} onChange={(e) => setAbn(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="industry">Industry</label>
            <input id="industry" value={industry} onChange={(e) => setIndustry(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="engagementType">Engagement type *</label>
            <select id="engagementType" value={engagementType} onChange={(e) => setEngagementType(e.target.value)}>
              {engagementTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
              <option value="Other">Other...</option>
            </select>
            {engagementType === 'Other' ? (
              <input
                style={{ marginTop: '0.4rem' }}
                value={engagementOther}
                onChange={(e) => setEngagementOther(e.target.value)}
                placeholder="Describe the engagement type"
              />
            ) : null}
          </div>
        </div>

        <div className="field">
          <label>Financial years</label>
          <div className="doc-checklist">
            {fyOptions.map((fy) => (
              <label key={fy} className="doc-check">
                <input
                  type="checkbox"
                  checked={!!checkedYears[fy]}
                  onChange={(e) => setCheckedYears((p) => ({ ...p, [fy]: e.target.checked }))}
                />
                {fy}
              </label>
            ))}
            <label className="doc-check">
              <input type="checkbox" checked={yearsOthersOn} onChange={(e) => setYearsOthersOn(e.target.checked)} />
              Others...
            </label>
          </div>
          {yearsOthersOn ? (
            <input
              style={{ marginTop: '0.5rem' }}
              value={yearsOthersText}
              onChange={(e) => setYearsOthersText(e.target.value)}
              placeholder="Comma separated, e.g. FY22, FY26 (Mar YTD)"
            />
          ) : null}
        </div>

        <h3 style={{ marginTop: '1rem' }}>Primary contact</h3>
        <div className="grid-2">
          <div className="field">
            <label htmlFor="contactName">Name *</label>
            <input id="contactName" value={contactName} onChange={(e) => setContactName(e.target.value)} required />
          </div>
          <div className="field">
            <label htmlFor="contactEmail">Email *</label>
            <input
              id="contactEmail"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="contactPhone">Phone</label>
            <input id="contactPhone" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="feeQuoted">Fee quoted ex GST (AUD)</label>
            <input
              id="feeQuoted"
              inputMode="decimal"
              value={feeQuoted}
              onChange={(e) => setFeeQuoted(e.target.value)}
              placeholder="e.g. 4500"
            />
          </div>
        </div>

        <div className="field">
          <label>Document requirements</label>
          <p className="muted" style={{ margin: '0 0 0.5rem', fontSize: '0.85rem' }}>
            Tick the documents to request. These appear as the checklist on the client upload portal.
          </p>
          <div className="doc-checklist">
            {docList.map((d) => (
              <label key={d} className="doc-check">
                <input
                  type="checkbox"
                  checked={!!checkedDocs[d]}
                  onChange={(e) => setCheckedDocs((p) => ({ ...p, [d]: e.target.checked }))}
                />
                {d}
              </label>
            ))}
            <label className="doc-check">
              <input type="checkbox" checked={othersOn} onChange={(e) => setOthersOn(e.target.checked)} />
              Others...
            </label>
          </div>
          {othersOn ? (
            <textarea
              rows={3}
              style={{ marginTop: '0.5rem' }}
              value={othersText}
              onChange={(e) => setOthersText(e.target.value)}
              placeholder="One per line, e.g. Franchise agreement, Shareholder agreement"
            />
          ) : null}
        </div>

        <div className="btn-row">
          <button type="submit" disabled={saving}>
            {saving ? 'Creating.' : 'Create job'}
          </button>
        </div>
      </form>
    </>
  );
}
